import type { AcceptanceProof, Licence } from "../../api/types";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatDate, formatTime } from "../../lib/time";

/** Texts with a part in bold, as in the prototype ("Je licentie loopt nog <b>5 dagen</b>, …"). */
export type Rich = readonly { text: string; bold?: boolean }[];

const parts = (day: string) => day.split("-").map(Number) as [number, number, number];

/** "01/11/2026" for a calendar day (YYYY-MM-DD): no time zone can move it to another day. */
export function dayDate(day: string): string {
  const [year, month, date] = parts(day);
  return `${String(date).padStart(2, "0")}/${String(month).padStart(2, "0")}/${String(year)}`;
}

/** "november 2026" */
export function monthName(day: string): string {
  const [year, month] = parts(day);
  return `${t.subscription.months[month - 1]!} ${String(year)}`;
}

/** "31 oktober" */
const dayAndMonth = (day: string) => {
  const [, month, date] = parts(day);
  return `${String(date)} ${t.subscription.months[month - 1]!}`;
};

/** The last day of the licence period that starts on this day (YYYY-MM-DD), as the server counts it. */
export function periodEnd(period: string): string {
  const [year, month, day] = parts(period);
  const daysNextMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(day, daysNextMonth) - 1)).toISOString().slice(0, 10);
}

/** "30/10/2026 14:05" */
export const dateTime = (moment: string) => `${formatDate(moment)} ${formatTime(moment)}`;

/** "€ 69,00 + 21% btw = € 83,49 per maand" */
export function priceText(licence: Licence): string {
  return t.subscription.plan.priceText(
    formatEuro(licence.priceCents),
    licence.vatRate,
    formatEuro(licence.monthlyCents),
  );
}

/** Status in the box Tafo-abonnement (prototype renderSubAdmin). */
export function statusText(licence: Licence): string {
  const text = t.subscription.plan;
  switch (licence.state) {
    case "expired":
      return text.expired;
    case "grace":
      return text.grace(dateTime(licence.graceUntil!));
    case "due":
      return text.due(licence.daysLeft, t.subscription.months[parts(licence.nextPeriod)[1] - 1]!);
    default:
      return text.ok;
  }
}

/** Whether the ⏳ line shows (to pay, under an exception or expired); red only when expired. */
export const showsDays = (licence: Licence) =>
  licence.state === "due" || licence.state === "grace" || licence.state === "expired";

/** The ⏳ line (prototype licDaysText). */
export function daysText(licence: Licence): Rich {
  const text = t.subscription.plan;
  if (licence.state === "grace") {
    return [{ text: text.graceText }, { text: dateTime(licence.graceUntil!), bold: true }, { text: "." }];
  }
  if (licence.state === "expired") {
    return [{ text: text.expiredText }, { text: dayDate(licence.paidUntil), bold: true }, { text: "." }];
  }
  const end = dayAndMonth(licence.paidUntil);
  if (licence.daysLeft <= 1) return [{ text: text.lastDay(end) }];
  return [{ text: text.daysLeft }, { text: text.days(licence.daysLeft), bold: true }, { text: text.until(end) }];
}

/** "✓ Akkoord v1.2 door Furkan Argu op 01/10/2026 20:14 via akkoordlink" */
export function acceptanceText(acceptance: AcceptanceProof): string {
  const text = t.subscription.contract;
  return text.accepted(acceptance.version, acceptance.name, dateTime(acceptance.at), text.via[acceptance.method]);
}

/** "Bewijs: eigenaarscode van Furkan (eigenaar) op de kassa · Windows · Chrome · IP … · …" */
export function proofText(acceptance: AcceptanceProof): string {
  const text = t.subscription.contract;
  const who = acceptance.account ? text.byOwnerCode(acceptance.account) : text.byLink(acceptance.email ?? "—");
  return text.proof(who, acceptance.device, acceptance.ip, acceptance.version, dateTime(acceptance.at));
}

/** "BE22 0019 0559 8847" */
export const ibanBlocks = (iban: string) =>
  iban
    .replace(/\s+/g, "")
    .toUpperCase()
    .replace(/(.{4})/g, "$1 ")
    .trim();
